
import { useEffect, useRef, useState } from 'react';

interface ScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

/**
 * Hook that observes an element and toggles visibility when it enters the viewport.
 *
 * @param options Configuration for the IntersectionObserver.
 * @returns An object containing a `ref` to attach to the target element and a boolean `isVisible`.
 */
export function useScrollAnimation(options: ScrollAnimationOptions = {}) {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -60px 0px',
    triggerOnce = true,
  } = options;

  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      // If the element is not yet mounted, do nothing.
      return;
    }

    // Create the observer with proper error handling.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    try {
      observer.observe(element);
    } catch (error) {
      console.error('Failed to observe element:', error);
    }

    // Cleanup on unmount or when dependencies change.
    return () => {
      try {
        observer.disconnect();
      } catch (error) {
        console.error('Failed to disconnect observer:', error);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
}

/**
 * Hook that provides staggered animation styles for a list of items.
 *
 * @param itemCount Number of items to animate (currently not used but kept for future extensibility).
 * @param options   Options passed to the underlying scroll animation hook.
 * @returns An object containing the `ref` to attach to the container, the visibility flag,
 *          and a `getItemStyle` function to retrieve per‑item styles.
 */
export function useStaggerAnimation(
  itemCount: number,
  options: ScrollAnimationOptions = {}
) {
  const { ref, isVisible } = useScrollAnimation(options);

  const getItemStyle = (index: number) => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
    transition: `opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${index *
      0.12}s, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${index *
      0.12}s`,
  });

  return { ref, isVisible, getItemStyle };
}
